import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { PDFDownloadLink, BlobProvider } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import PDFTemplate from './PDFTemplate';
import DonationWidget from './DonationWidget';
import './ResultsModal.css';

/**
 * ResultsModal - Overlay modal for displaying analysis results
 *
 * Props:
 * - report: string - Raw report markdown
 * - styledReport: string - Report with color styling applied
 * - disclaimer: string - Legal disclaimer text
 * - containsNDA: boolean - Whether document contains NDA
 * - ndaReport: string | null - State-specific NDA information
 * - onClose: () => void - Handler to close modal
 */
export function ResultsModal({
  report,
  styledReport,
  disclaimer,
  containsNDA,
  ndaReport,
  onClose,
}) {
  const { i18n } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  const handlePrint = (pdfUrl) => {
    if (!pdfUrl) return;

    // Open PDF in new window and print
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  const handleScrollTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Focus trap
  useEffect(() => {
    if (!modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    firstFocusableRef.current = focusableElements[0];
    lastFocusableRef.current = focusableElements[focusableElements.length - 1];

    // Focus first element on mount
    firstFocusableRef.current?.focus();

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusableRef.current) {
          e.preventDefault();
          lastFocusableRef.current?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusableRef.current) {
          e.preventDefault();
          firstFocusableRef.current?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, []);

  // Handle scroll detection for "scroll to top" button
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollTop = contentRef.current.scrollTop;
        setShowScrollTop(scrollTop > 300);
      }
    };

    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', handleScroll);
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Screen reader announcement
  useEffect(() => {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = 'Analysis report loaded. Use Tab to navigate through the report.';
    document.body.appendChild(announcement);

    return () => {
      document.body.removeChild(announcement);
    };
  }, []);

  return (
    <div
      className={`results-modal-backdrop ${isClosing ? 'closing' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className={`results-modal ${isClosing ? 'closing' : ''}`}
        ref={modalRef}
      >
        <button
          className="modal-close-button"
          onClick={handleClose}
          aria-label="Close modal"
          title="Close (Esc)"
        >
          &times;
        </button>

        <div className="modal-content" ref={contentRef}>
          <h2 id="modal-title" className="modal-title">Your Analysis Report</h2>

          {/* Report Section */}
          <div className="report-section">
            <div className="report-content">
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {styledReport || report}
              </ReactMarkdown>
            </div>
          </div>

          {/* Section Divider */}
          <div className="section-divider" aria-hidden="true"></div>

          {/* Disclaimer Section */}
          {disclaimer && (
            <>
              <div className="disclaimer-section">
                <h3>
                  <span className="icon-warning" aria-hidden="true">&#9888;</span>
                  Important Disclaimer
                </h3>
                <p>{disclaimer}</p>
              </div>
              <div className="section-divider" aria-hidden="true"></div>
            </>
          )}

          {/* NDA Info Section */}
          {containsNDA && ndaReport && (
            <>
              <div className="nda-section">
                <h3>
                  <span className="icon-info" aria-hidden="true">&#9432;</span>
                  State NDA Information
                </h3>
                <div className="nda-content">
                  <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                    {ndaReport}
                  </ReactMarkdown>
                </div>
              </div>
              <div className="section-divider" aria-hidden="true"></div>
            </>
          )}

          {/* Action Buttons */}
          <div className="modal-actions">
            <PDFDownloadLink
              document={
                <PDFTemplate
                  report={report}
                  disclaimer={disclaimer}
                  containsNDA={containsNDA}
                  ndaReport={ndaReport}
                  language={i18n.language}
                />
              }
              fileName="LOV_Document_Analysis.pdf"
              className="action-button pdf-button"
              aria-label="Download report as PDF"
            >
              {({ loading }) => (
                <>
                  <span className="button-icon" aria-hidden="true">&#8595;</span>
                  {loading ? 'PREPARING PDF...' : 'DOWNLOAD PDF'}
                </>
              )}
            </PDFDownloadLink>

            <BlobProvider
              document={
                <PDFTemplate
                  report={report}
                  disclaimer={disclaimer}
                  containsNDA={containsNDA}
                  ndaReport={ndaReport}
                  language={i18n.language}
                />
              }
            >
              {({ url, loading }) => (
                <button
                  className="action-button print-button"
                  onClick={() => handlePrint(url)}
                  disabled={loading || !url}
                  aria-label="Print report"
                >
                  <span className="button-icon" aria-hidden="true">&#128424;</span>
                  {loading ? 'LOADING...' : 'PRINT'}
                </button>
              )}
            </BlobProvider>
          </div>

          {/* Donation Widget */}
          <div className="donation-section">
            <DonationWidget />
          </div>
        </div>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            className="scroll-top-button"
            onClick={handleScrollTop}
            aria-label="Scroll to top of report"
            title="Scroll to top"
          >
            <span aria-hidden="true">&#8679;</span>
          </button>
        )}
      </div>
    </div>
  );
}
